import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { BORGHI } from '@/mocks/borghi';

const FEATURED_SLUGS = ['osimo', 'sirolo', 'offagna', 'castelfidardo', 'staffolo', 'torre-di-palme'];
const featured = BORGHI.filter((b) => FEATURED_SLUGS.includes(b.slug));

const BorghiSection = () => {
  const header = useScrollAnimation();
  const grid = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="borghi" className="bg-ivory-50 py-24 md:py-32 px-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={header.ref}
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6 fade-in-up ${header.isVisible ? 'visible' : ''}`}
        >
          <div>
            <p className="font-sans text-xs tracking-widest2 uppercase text-terracotta-300 mb-4">
              I borghi delle Marche
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-anthracite-800 font-light leading-tight max-w-xl">
              Luoghi dove la vita<br />
              <em className="cormorant-italic not-italic text-terracotta-300">ritrova il suo ritmo</em>
            </h2>
          </div>
          <Link
            to="/borghi"
            className="inline-flex items-center gap-2 font-sans text-sm tracking-widest uppercase text-anthracite-600 hover:text-terracotta-300 transition-colors whitespace-nowrap group"
          >
            Esplora tutti i borghi
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
          </Link>
        </div>

        {/* Grid */}
        <div
          ref={grid.ref}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 fade-in ${grid.isVisible ? 'visible' : ''}`}
        >
          {featured.map((borgo, i) => (
            <Link
              key={borgo.slug}
              to={`/borghi/${borgo.slug}`}
              className="group relative overflow-hidden rounded-lg cursor-pointer block"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {/* Image */}
              <div className="relative w-full h-64 overflow-hidden">
                <img
                    src={borgo.image}
                    alt={`${borgo.name} — borghi Marche`}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                {/* Zona badge */}
                <span className="absolute top-4 left-4 font-sans text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/30">
                  {borgo.zona}
                </span>

                {/* Provincia */}
                <span className="absolute top-4 right-4 font-sans text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-terracotta-300/80 text-white">
                  {borgo.provincia}
                </span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-xl text-white font-light mb-1">
                  {borgo.name}
                </h3>
                <p className="font-sans text-xs text-white/75 leading-relaxed line-clamp-2">
                  {borgo.tagline}
                </p>

                {/* Highlights */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {borgo.highlights.slice(0, 2).map((h) => (
                    <span
                      key={h}
                      className="font-sans text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-white/15 text-white/90 border border-white/20"
                    >
                      {h}
                    </span>
                  ))}
                </div>

                {/* Arrow */}
                <div className="mt-3 flex items-center gap-1.5 text-white/60 group-hover:text-white transition-colors">
                  <span className="font-sans text-xs tracking-widest uppercase">Scopri</span>
                  <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link
            to="/borghi"
            className="inline-flex items-center gap-2 font-sans text-sm tracking-widest uppercase px-8 py-3.5 rounded-md border border-anthracite-300 text-anthracite-600 hover:border-terracotta-300 hover:text-terracotta-300 transition-all whitespace-nowrap"
          >
            Vedi tutti i borghi
            <i className="ri-map-pin-line"></i>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default BorghiSection;
